import React, { useState, useEffect, useRef } from 'react';
import { getVendors, addVendor, updateVendor, deleteVendor } from '../firebase';

const ADMIN_EMAIL = 'ptrakkides@gmail.com';

const CATEGORIES = [
  { id: 'photography', label: 'Photography' },
  { id: 'flowers',     label: 'Flowers & Decor' },
  { id: 'venues',      label: 'Venues' },
  { id: 'catering',    label: 'Catering' },
  { id: 'music',       label: 'Music' },
  { id: 'travel',      label: 'Travel & Honeymoon' },
  { id: 'registry',    label: 'Gift Registry' },
  { id: 'other',       label: 'Other' },
];

const todayStr = () => new Date().toLocaleDateString('en-CA');

const EMPTY_FORM = {
  name: '',
  category: 'photography',
  description: '',
  href: '',
  icon: 'fa-store',
  color: '#1a365d',
  featured: false,
  hidden: false,
  visibleFrom: todayStr(),
  visibleUntil: '',
  permanent: true,
  contactEmail: '',
  contactPhone: '',
};

function formatDate(iso) {
  if (!iso) return '∞';
  return new Date(iso + 'T00:00:00').toLocaleDateString('el-GR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function visibilityStatus(v) {
  if (v.hidden) return 'hidden';
  const today = todayStr();
  if (v.visibleFrom && v.visibleFrom > today) return 'scheduled';
  if (v.visibleUntil && v.visibleUntil < today) return 'expired';
  return 'active';
}

export default function AdminVendors({ user }) {
  const [vendors, setVendors]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');
  const formRef = useRef(null);

  const isAdmin = user?.email === ADMIN_EMAIL;

  useEffect(() => {
    if (!isAdmin) return;
    load();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    setLoading(true);
    const data = await getVendors();
    setVendors(data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    setLoading(false);
  };

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({
      name:         v.name         || '',
      category:     v.category     || 'photography',
      description:  v.description  || '',
      href:         v.href         || '',
      icon:         v.icon         || 'fa-store',
      color:        v.color        || '#1a365d',
      featured:     !!v.featured,
      hidden:       !!v.hidden,
      visibleFrom:  v.visibleFrom  || todayStr(),
      visibleUntil: v.visibleUntil || '',
      permanent:    !v.visibleUntil,
      contactEmail: v.contactEmail || '',
      contactPhone: v.contactPhone || '',
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.description.trim()) {
      setError('Name and description are required.');
      return;
    }
    setSaving(true);
    setError('');
    const { permanent, ...payload } = form;
    const data = { ...payload, visibleUntil: permanent ? '' : payload.visibleUntil };

    let result;
    if (editingId) {
      result = await updateVendor(editingId, data);
      if (result.success) {
        setVendors(prev => prev.map(v => v.id === editingId ? { ...v, ...data } : v));
        setEditingId(null);
        setForm(EMPTY_FORM);
        setSuccess('Vendor updated.');
      }
    } else {
      result = await addVendor(data);
      if (result.success) {
        setForm(EMPTY_FORM);
        setSuccess('Vendor added.');
        await load();
      }
    }

    if (!result.success) setError(result.error);
    else setTimeout(() => setSuccess(''), 3000);
    setSaving(false);
  };

  const handleToggleHidden = async (v) => {
    const newHidden = !v.hidden;
    const result = await updateVendor(v.id, { hidden: newHidden });
    if (result.success) {
      setVendors(prev => prev.map(x => x.id === v.id ? { ...x, hidden: newHidden } : x));
    }
  };

  const handleDelete = async (id) => {
    if (editingId === id) cancelEdit();
    const result = await deleteVendor(id);
    if (result.success) setVendors(prev => prev.filter(v => v.id !== id));
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-lock fs-1 text-muted mb-3 d-block"></i>
        <h4 className="text-muted">Access denied</h4>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))' }}>
        <div className="card-body p-4 text-white">
          <h4 className="fw-bold mb-0"><i className="fas fa-cogs me-2"></i>Vendor Admin</h4>
          <p className="mb-0 opacity-75 small">Add, edit or manage vendor visibility</p>
        </div>
      </div>

      {error   && <div className="alert alert-danger  border-0 rounded-3 mb-3">{error}</div>}
      {success && <div className="alert alert-success border-0 rounded-3 mb-3">{success}</div>}

      {/* ── Form (Add / Edit) ───────────────────────────── */}
      <div ref={formRef} className="card border-0 shadow-sm mb-4" style={{ borderRadius: '16px', border: editingId ? '2px solid var(--primary-color)' : undefined }}>
        <div className="card-header bg-white border-0 py-3 d-flex align-items-center justify-content-between">
          <h5 className="mb-0 fw-bold text-primary">
            <i className={`fas ${editingId ? 'fa-edit' : 'fa-plus'} me-2`}></i>
            {editingId ? 'Edit Vendor' : 'Add Vendor'}
          </h5>
          {editingId && (
            <button className="btn btn-outline-secondary btn-sm rounded-pill px-3" onClick={cancelEdit}>
              <i className="fas fa-times me-1"></i>Cancel
            </button>
          )}
        </div>
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">

              {/* Basic info */}
              <div className="col-md-6">
                <label className="form-label fw-semibold">Name *</label>
                <input type="text" className="form-control" placeholder="e.g. Booking.com"
                  value={form.name} onChange={e => set('name', e.target.value)} required />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Category *</label>
                <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">Description *</label>
                <input type="text" className="form-control" placeholder="Short description shown on the card"
                  value={form.description} onChange={e => set('description', e.target.value)} required />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold">URL (affiliate link)</label>
                <input type="url" className="form-control font-monospace" placeholder="https://..."
                  value={form.href} onChange={e => set('href', e.target.value)} />
              </div>

              {/* Appearance */}
              <div className="col-md-4">
                <label className="form-label fw-semibold">FontAwesome Icon</label>
                <input type="text" className="form-control font-monospace" placeholder="fa-camera"
                  value={form.icon} onChange={e => set('icon', e.target.value)} />
                <small className="text-muted">
                  <a href="https://fontawesome.com/icons" target="_blank" rel="noopener noreferrer">browse icons</a>
                </small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Brand Color</label>
                <div className="d-flex align-items-center gap-2">
                  <input type="color" className="form-control form-control-color"
                    value={form.color} onChange={e => set('color', e.target.value)}
                    style={{ width: 52, height: 38 }} />
                  <span className="badge rounded-pill px-3 py-2 fw-semibold" style={{ background: form.color, color: '#fff' }}>
                    <i className={`fas ${form.icon} me-1`}></i>{form.name || 'Preview'}
                  </span>
                </div>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Options</label>
                <div className="d-flex flex-column gap-1 mt-1">
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="featured"
                      checked={form.featured} onChange={e => set('featured', e.target.checked)} />
                    <label className="form-check-label" htmlFor="featured">Show "Featured" badge</label>
                  </div>
                  <div className="form-check">
                    <input type="checkbox" className="form-check-input" id="hidden"
                      checked={form.hidden} onChange={e => set('hidden', e.target.checked)} />
                    <label className="form-check-label text-muted" htmlFor="hidden">Hidden (not visible to users)</label>
                  </div>
                </div>
              </div>

              {/* Visibility date range */}
              <div className="col-12">
                <hr className="my-1" />
                <label className="form-label fw-semibold mb-2">
                  <i className="fas fa-calendar-alt me-1 text-primary"></i>Visibility Period
                </label>
                <div className="row g-2 align-items-end">
                  <div className="col-md-4">
                    <label className="form-label small text-muted mb-1">Start date</label>
                    <input type="date" className="form-control"
                      value={form.visibleFrom} onChange={e => set('visibleFrom', e.target.value)} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label small text-muted mb-1">End date</label>
                    <input type="date" className="form-control"
                      value={form.permanent ? '' : form.visibleUntil}
                      onChange={e => set('visibleUntil', e.target.value)}
                      disabled={form.permanent}
                      min={form.visibleFrom || todayStr()}
                    />
                  </div>
                  <div className="col-md-4">
                    <div className="form-check mt-1">
                      <input type="checkbox" className="form-check-input" id="permanent"
                        checked={form.permanent}
                        onChange={e => { set('permanent', e.target.checked); if (e.target.checked) set('visibleUntil', ''); }} />
                      <label className="form-check-label fw-semibold" htmlFor="permanent">
                        <i className="fas fa-infinity me-1 text-success"></i>No end date
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact info */}
              <div className="col-12">
                <hr className="my-1" />
                <label className="form-label fw-semibold mb-2">
                  <i className="fas fa-address-card me-1 text-primary"></i>Contact Info
                  <span className="text-muted fw-normal ms-1 small">(shown on the vendor card)</span>
                </label>
                <div className="row g-2">
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="fas fa-envelope text-muted"></i></span>
                      <input type="email" className="form-control" placeholder="contact@example.com"
                        value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="input-group">
                      <span className="input-group-text bg-light"><i className="fas fa-phone text-muted"></i></span>
                      <input type="text" className="form-control" placeholder="+357 99 123456"
                        value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 text-end">
                {editingId && (
                  <button type="button" className="btn btn-outline-secondary rounded-pill px-4 me-2" onClick={cancelEdit}>
                    Cancel
                  </button>
                )}
                <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={saving}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Saving...</>
                    : editingId
                      ? <><i className="fas fa-save me-1"></i>Update Vendor</>
                      : <><i className="fas fa-plus me-1"></i>Add Vendor</>}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* ── Vendor List ─────────────────────────────────── */}
      <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
        <div className="card-header bg-white border-0 py-3 d-flex align-items-center justify-content-between">
          <h5 className="mb-0 fw-bold text-primary"><i className="fas fa-list me-2"></i>Vendors ({vendors.length})</h5>
          <button className="btn btn-outline-secondary btn-sm rounded-pill" onClick={load}>
            <i className="fas fa-sync me-1"></i>Refresh
          </button>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
          ) : vendors.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fas fa-inbox fs-2 mb-2 d-block opacity-25"></i>
              <p className="mb-0">No vendors yet. Add your first one above.</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {vendors.map(v => {
                const cat    = CATEGORIES.find(c => c.id === v.category);
                const status = visibilityStatus(v);
                const statusBadge = {
                  active:    { cls: 'bg-success',          label: 'Live'      },
                  hidden:    { cls: 'bg-dark',             label: 'Hidden'    },
                  scheduled: { cls: 'bg-warning text-dark', label: 'Scheduled' },
                  expired:   { cls: 'bg-secondary',        label: 'Expired'   },
                }[status];
                const isEditing = editingId === v.id;

                return (
                  <div key={v.id}
                    className="list-group-item px-4 py-3"
                    style={{ background: isEditing ? 'var(--gray-50)' : undefined, opacity: v.hidden ? 0.6 : 1 }}
                  >
                    <div className="d-flex align-items-start gap-3">
                      {/* Icon */}
                      <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{ width: 40, height: 40, background: `${v.color || '#1a365d'}18` }}>
                        <i className={`fas ${v.icon || 'fa-store'}`} style={{ color: v.color || '#1a365d' }}></i>
                      </div>

                      {/* Info */}
                      <div className="flex-grow-1 min-w-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <span className="fw-semibold">{v.name}</span>
                          <span className="badge bg-light text-muted small">{cat?.label || v.category}</span>
                          {v.featured && <span className="badge" style={{ background: v.color, fontSize: '0.65rem', color: '#fff' }}>Featured</span>}
                          <span className={`badge ${statusBadge.cls}`} style={{ fontSize: '0.65rem' }}>{statusBadge.label}</span>
                        </div>
                        <p className="text-muted small mb-1 text-truncate">{v.description}</p>
                        <div className="d-flex flex-wrap gap-3 small text-muted mb-1">
                          <span>
                            <i className="fas fa-calendar-alt me-1"></i>
                            {formatDate(v.visibleFrom)} → {v.visibleUntil ? formatDate(v.visibleUntil) : <span className="text-success fw-semibold">∞</span>}
                          </span>
                        </div>
                        {(v.contactEmail || v.contactPhone) && (
                          <div className="d-flex flex-wrap gap-3 small">
                            {v.contactEmail && (
                              <a href={`mailto:${v.contactEmail}`} className="text-muted text-decoration-none">
                                <i className="fas fa-envelope me-1"></i>{v.contactEmail}
                              </a>
                            )}
                            {v.contactPhone && (
                              <a href={`tel:${v.contactPhone}`} className="text-muted text-decoration-none">
                                <i className="fas fa-phone me-1"></i>{v.contactPhone}
                              </a>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="d-flex flex-column gap-2 flex-shrink-0 align-items-end">
                        {/* Visibility toggle */}
                        <div className="d-flex align-items-center gap-2">
                          <small className="text-muted">{v.hidden ? 'Hidden' : 'Visible'}</small>
                          <div className="form-check form-switch mb-0">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              checked={!v.hidden}
                              onChange={() => handleToggleHidden(v)}
                              style={{ width: '2.5em', height: '1.3em', cursor: 'pointer' }}
                            />
                          </div>
                        </div>
                        {/* Edit / Delete */}
                        <div className="d-flex gap-2">
                          <button
                            className={`btn btn-sm rounded-pill px-3 ${isEditing ? 'btn-primary' : 'btn-outline-primary'}`}
                            onClick={() => isEditing ? cancelEdit() : startEdit(v)}
                          >
                            <i className={`fas ${isEditing ? 'fa-times' : 'fa-edit'} me-1`}></i>
                            {isEditing ? 'Cancel' : 'Edit'}
                          </button>
                          <button className="btn btn-outline-danger btn-sm rounded-pill px-3"
                            onClick={() => handleDelete(v.id)}>
                            <i className="fas fa-trash-alt me-1"></i>Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
